// HTTP client that uses Capacitor HTTP on mobile to bypass CORS
import { CapacitorHttp, Capacitor } from '@capacitor/core';
import logger from './logger';
import apiErrorHandler from './apiErrorHandler';

const isCapacitor = Capacitor.isNativePlatform();

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === 'string' ? result.split(',')[1] : '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function convertFormData(formData) {
  const entries = [];
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      const base64 = await readFileAsBase64(value);
      entries.push({
        key,
        value: base64,
        type: 'base64File',
        contentType: value.type || 'application/octet-stream',
        fileName: value.name || 'file',
      });
    } else if (value instanceof Blob) {
      const base64 = await readFileAsBase64(value);
      entries.push({
        key,
        value: base64,
        type: 'base64File',
        contentType: value.type || 'application/octet-stream',
        fileName: 'blob',
      });
    } else {
      entries.push({ key, value: String(value), type: 'string' });
    }
  }
  return entries;
}

function base64ToBlob(base64, contentType) {
  const byteString = window.atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: contentType || 'application/octet-stream' });
}

/**
 * HTTP client that bypasses CORS on mobile by using Capacitor's native HTTP
 * with integrated error handling and logging
 */
export async function httpRequest(url, options = {}) {
  const startTime = Date.now();
  const method = options.method || 'GET';
  const context = { url, method, isCapacitor };

  try {
    if (isCapacitor && CapacitorHttp) {
      // Use Capacitor HTTP on mobile to bypass CORS
      logger.debug('Making Capacitor HTTP request', { url, method });

      // Parse body if it's a string (JSON)
      let requestData = options.body;
      if (typeof requestData === 'string') {
        try {
          requestData = JSON.parse(requestData);
        } catch (e) {
          // Keep as string if not JSON
        }
      }

      const requestConfig = {
        url,
        method: method,
        headers: options.headers || {},
      };

      // Capacitor's native HTTP only accepts string or JSON data.
      // Convert FormData into the native Capacitor form-data representation.
      if (requestData instanceof FormData) {
        requestConfig.data = await convertFormData(requestData);
        requestConfig.dataType = 'formData';
      } else if (requestData !== undefined && requestData !== null) {
        requestConfig.data = requestData;
      }

      if (options.responseType) {
        requestConfig.responseType = options.responseType;
      }

      const response = await CapacitorHttp.request(requestConfig);

      const duration = Date.now() - startTime;
      const contentType = ((response.headers || {})['content-type'] || (response.headers || {})['Content-Type'] || '').toString().split(';')[0];

      // Convert Capacitor response to fetch-like response
      const fetchResponse = {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        statusText: response.statusText || '',
        url: url,
        json: async () => {
          if (typeof response.data === 'string') {
            return JSON.parse(response.data);
          }
          return response.data;
        },
        text: async () => {
          if (typeof response.data === 'string') {
            return response.data;
          }
          return JSON.stringify(response.data);
        },
        blob: async () => {
          if (response.data instanceof Blob) {
            return response.data;
          }
          if (response.data instanceof ArrayBuffer) {
            return new Blob([response.data], { type: contentType });
          }
          if (typeof response.data === 'string' && requestConfig.responseType === 'blob') {
            return base64ToBlob(response.data, contentType);
          }
          throw new Error('Binary response not available on this platform');
        },
        headers: new Headers(response.headers || {}),
      };

      // Log successful request
      if (fetchResponse.ok) {
        logger.api(method, url, response.status, duration, { platform: 'capacitor' });
      } else {
        // Handle error response
        const error = await apiErrorHandler.handleResponseError(fetchResponse, {
          ...context,
          platform: 'capacitor',
          duration
        });
        throw error;
      }

      return fetchResponse;
    } else {
      // Use standard fetch on web
      logger.debug('Making fetch request', { url, method });
      const fetchOptions = {
        ...options,
        credentials: 'include', // Send httpOnly cookies automatically
      };

      // Auto-set JSON Content-Type when the body is a JSON string and no
      // Content-Type header has been provided. FastAPI/Pydantic endpoints
      // require this header to parse the request body.
      const hasContentType =
        fetchOptions.headers &&
        Object.keys(fetchOptions.headers).some(
          (k) => k.toLowerCase() === 'content-type'
        );
      if (
        !hasContentType &&
        typeof fetchOptions.body === 'string' &&
        (fetchOptions.body.trim().startsWith('{') || fetchOptions.body.trim().startsWith('['))
      ) {
        fetchOptions.headers = {
          ...fetchOptions.headers,
          'Content-Type': 'application/json',
        };
      }

      return await apiErrorHandler.fetchWithErrorHandling(url, fetchOptions, context);
    }
  } catch (error) {
    const duration = Date.now() - startTime;

    // If it's already a standardized error, re-throw it
    if (error.status !== undefined) {
      throw error;
    }

    // Handle network errors
    const networkError = apiErrorHandler.handleNetworkError(error, {
      ...context,
      duration
    });
    throw networkError;
  }
}
